/* ------------------------------------------------------------------
   Cart confirmations — checkout gate, gated express checkout and
   adapter upsell. Markup: snippets/cart-confirmations.liquid.

   Which notices are shown, whether they are pre-confirmed and whether
   checkout is blocked is all decided in Liquid and re-rendered by the
   sections API on every cart change. This script only reacts to
   clicks: it keeps the checkout gate in sync with the checkboxes,
   persists the confirmations as cart attributes (so they survive the
   re-render and show up on the order) and explains why checkout is
   blocked when someone tries anyway.

   Every listener is delegated on `document`, and all lookups are
   scoped to the closest [data-cart-gate-scope] (drawer footer or cart
   page recap), so replacing the drawer markup can never strand a
   handler and the drawer and the cart page never affect each other.
   ------------------------------------------------------------------ */
(function() {
  'use strict';

  if (window.__cartConfirmationsLoaded) return;
  window.__cartConfirmationsLoaded = true;

  function isGated(scope) {
    var checks = scope.querySelectorAll('[data-gate-check]');

    for (var i = 0; i < checks.length; i++) {
      if (!checks[i].checked) return true;
    }

    return false;
  }

  function updateGate(scope) {
    var gated = isGated(scope);

    scope.querySelectorAll('[data-checkout-gate]').forEach(function(gate) {
      gate.toggleAttribute('data-gated', gated);

      var button = gate.querySelector('[name="checkout"]');
      if (button) button.setAttribute('aria-disabled', gated ? 'true' : 'false');
    });

    scope.querySelectorAll('[data-gate-express]').forEach(function(express) {
      express.toggleAttribute('data-gated', gated);
    });

    if (!gated) {
      scope.querySelectorAll('[data-gate-hint]').forEach(function(hint) { hint.hidden = true; });
    }
  }

  function showGateHint(scope) {
    var hint = scope.querySelector('[data-gate-hint]');
    if (hint) hint.hidden = false;

    var unchecked = Array.prototype.find.call(scope.querySelectorAll('[data-gate-check]'), function(check) {
      return !check.checked;
    });

    if (!unchecked) return;

    var label = unchecked.closest('[data-gate-label]') || unchecked.parentElement;
    label.scrollIntoView({ behavior: 'smooth', block: 'center' });
    label.classList.remove('is-attention');
    void label.offsetWidth; /* restart the animation */
    label.classList.add('is-attention');
    unchecked.focus({ preventScroll: true });
  }

  /* Persisted as a visible cart attribute, so the confirmation is part of the order record */
  async function setCartAttribute(name, confirmed) {
    var attributes = {};
    attributes[name] = confirmed ? new Date().toISOString() : '';

    try {
      await fetch(Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: attributes })
      });
    } catch(e) {
      /* The checkbox state is still live in the DOM — the attribute is only the audit trail */
      console.warn('[cart-confirmations] could not persist ' + name, e);
    }
  }

  async function addAdapterToCart(button) {
    var variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      var sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true, detail: { sections: sections }
      }));

      var body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      if (sections.length) body.append('sections', sections.join(','));
      body.append('sections_url', window.location.pathname);

      var response = await fetch(Shopify.routes.root + 'cart/add.js', {
        method: 'POST', body: body, headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (response.ok) {
        /* The cart page is rendered server-side only, so it needs a reload to show the new line */
        if (button.closest('[data-cart-gate-scope="page"]')) {
          window.location.reload();
          return;
        }

        var added = await response.json();
        var cart = await (await fetch(Shopify.routes.root + 'cart.js')).json();
        if (added.sections) cart.sections = added.sections;

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cart }
        }));
      }
    } catch(e) {
      console.warn('[cart-confirmations] adapter add error', e);
    }

    button.removeAttribute('aria-busy');
    button.disabled = false;
  }

  /* Express buttons are injected by Shopify's script; after the drawer footer has been re-rendered they need a re-init */
  function initExpressButtons() {
    try {
      if (window.Shopify && Shopify.StorefrontExpressButtons && Shopify.StorefrontExpressButtons.initialize) {
        Shopify.StorefrontExpressButtons.initialize();
      }
    } catch(e) {
      console.warn('[cart-confirmations] express buttons init error', e);
    }
  }

  document.addEventListener('change', function(event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches('[data-gate-check]')) return;

    var name = target.getAttribute('data-gate-check');
    var checked = target.checked;

    /* Keep the same confirmation in sync if both the drawer and the cart page are in the DOM */
    document.querySelectorAll('[data-gate-check="' + name + '"]').forEach(function(check) {
      check.checked = checked;
      var label = check.closest('[data-gate-label]');
      if (label) label.classList.toggle('is-checked', checked);
    });

    setCartAttribute(name, checked);
    document.querySelectorAll('[data-cart-gate-scope]').forEach(updateGate);
  });

  document.addEventListener('click', function(event) {
    if (!event.target.closest) return;

    var adapterButton = event.target.closest('[data-adapter-add]');
    if (adapterButton) {
      addAdapterToCart(adapterButton);
      return;
    }

    var overlay = event.target.closest('[data-gate-overlay]');
    if (overlay) {
      var overlayScope = overlay.closest('[data-cart-gate-scope]');
      if (overlayScope) showGateHint(overlayScope);
      return;
    }

    var checkoutButton = event.target.closest('[data-checkout-gate] [name="checkout"]');
    if (checkoutButton) {
      var scope = checkoutButton.closest('[data-cart-gate-scope]');

      if (scope && isGated(scope)) {
        event.preventDefault();
        showGateHint(scope);
      }
    }
  });

  /* Safety net for submits that don't go through a click (e.g. Enter key) */
  document.addEventListener('submit', function(event) {
    var submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;

    var scope = submitter.closest('[data-cart-gate-scope]');

    if (scope && isGated(scope)) {
      event.preventDefault();
      showGateHint(scope);
    }
  }, true);

  document.addEventListener('cart:change', function() {
    /* The drawer footer is replaced synchronously by CartDrawer on cart:change; wait for it */
    setTimeout(initExpressButtons, 50);
  });

  document.addEventListener('cart:refresh', function() {
    setTimeout(initExpressButtons, 500);
  });
})();
